// Money is always whole UGX held in a JS number. UGX has no minor unit and
// realistic personal amounts stay far below Number.MAX_SAFE_INTEGER, so
// integers are exact. Never divide without rounding back to an integer.

const full = new Intl.NumberFormat("en-UG", { maximumFractionDigits: 0 });

export function assertUGX(amount: number): void {
	if (!Number.isSafeInteger(amount)) {
		throw new RangeError(`UGX amount must be a safe integer, got ${amount}`);
	}
}

/** `UGX 15,000`, or `-UGX 15,000` for negatives. */
export function formatUGX(amount: number): string {
	assertUGX(amount);
	const sign = amount < 0 ? "-" : "";
	return `${sign}UGX ${full.format(Math.abs(amount))}`;
}

/** Compact form for cards and charts, ported from connect-v2's fmtUGX: `UGX 1.2M`. */
export function formatUGXCompact(amount: number): string {
	assertUGX(amount);
	const abs = Math.abs(amount);
	const sign = amount < 0 ? "-" : "";
	if (abs >= 1_000_000_000) return `${sign}UGX ${trim(abs / 1_000_000_000)}B`;
	if (abs >= 1_000_000) return `${sign}UGX ${trim(abs / 1_000_000)}M`;
	if (abs >= 10_000) return `${sign}UGX ${trim(abs / 1_000)}K`;
	return formatUGX(amount);
}

function trim(n: number): string {
	return n.toFixed(1).replace(/\.0$/, "");
}

/**
 * Whole UGX or null. Decimals are only allowed with a k/m suffix (1.5k) and
 * must land on a whole shilling, so floats never reach money (CLAUDE.md rule).
 */
export function toUGX(digits: string, suffix?: string): number | null {
	const plain = digits.replace(/,/g, "");
	const multiplier = suffix?.toLowerCase() === "m" ? 1_000_000 : suffix ? 1_000 : 1;
	const [whole, frac = ""] = plain.split(".");
	if (frac && multiplier === 1) return null;
	// Integer arithmetic on the digit strings avoids 1.1 * 1000 = 1100.0000000000002.
	const scale = 10 ** frac.length;
	if (multiplier % scale !== 0) return null;
	const amount = Number(whole) * multiplier + (Number(frac || "0") * multiplier) / scale;
	if (!Number.isSafeInteger(amount) || amount < 1) return null;
	return amount;
}

const AMOUNT = /^(?:ugx|ush|shs?)?\s*(\d{1,3}(?:[,\s]\d{3})+|\d+(?:\.\d+)?)\s*([km])?$/i;

/**
 * Parses what someone types into an amount field: `15000`, `15,000`,
 * `15 000`, `UGX 15,000`, `15k`, `1.5m`. Returns whole UGX, or null when it
 * is not a positive whole-shilling amount (`10.5` is rejected, never rounded).
 */
export function parseUGX(input: string): number | null {
	const m = AMOUNT.exec(input.trim());
	if (!m) return null;
	return toUGX(m[1].replace(/\s/g, ","), m[2]);
}
